import { SHOPIFY_GRAPHQL_API_ENDPOINT } from '@frontend/storefront/lib/constants';
import {
  Cart,
  Connection,
  ShopifyCart,
} from '@frontend/storefront/lib/shopify/types';
import { isShopifyError } from '@frontend/storefront/lib/type-guards';
import { ensureStartsWith } from '@frontend/storefront/lib/utils';

// Runs both at build time and in the browser (the cart), so it only uses the
// public Storefront API token. That token is meant to be exposed.
export const domain = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN
  ? ensureStartsWith(import.meta.env.VITE_SHOPIFY_STORE_DOMAIN, 'https://')
  : '';
export const endpoint = domain
  ? `${domain}${SHOPIFY_GRAPHQL_API_ENDPOINT}`
  : '';
const key = import.meta.env.VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN!;

type ExtractVariables<T> = T extends { variables: object }
  ? T['variables']
  : never;

export async function shopifyFetch<T>({
  query,
  variables,
}: {
  query: string;
  variables?: ExtractVariables<T>;
}): Promise<{ status: number; body: T } | never> {
  try {
    if (!endpoint) {
      throw new Error(
        'NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN environment variable is not set',
      );
    }

    const result = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': key,
      },
      body: JSON.stringify({
        ...(query && { query }),
        ...(variables && { variables }),
      }),
    });

    const body = await result.json();

    if (body.errors) {
      throw body.errors[0];
    }

    return {
      status: result.status,
      body,
    };
  } catch (e) {
    if (isShopifyError(e)) {
      throw {
        cause: e.cause?.toString() || 'unknown',
        status: e.status || 500,
        message: e.message,
        query,
      };
    }

    throw {
      error: e,
      query,
    };
  }
}

export const removeEdgesAndNodes = <T>(array: Connection<T>): T[] => {
  return array.edges.map((edge) => edge?.node);
};

export const reshapeCart = (cart: ShopifyCart): Cart => {
  if (!cart.cost?.totalTaxAmount) {
    cart.cost.totalTaxAmount = {
      amount: '0.0',
      currencyCode: cart.cost.totalAmount.currencyCode,
    };
  }

  return {
    ...cart,
    lines: removeEdgesAndNodes(cart.lines),
  };
};
